import React, { useEffect, useMemo, useState } from 'react'
import useTrans from '@hooks/useTrans'
import { cn, dateTimeFormat, getUserType } from '@utils/safeTs'
import { LogStatoOrdineType, LogStatoPosizionePersonalizzazioneOrdineType, OrdineDetailType, OrdineType, RootStateType } from '@utils/types'
import MyAccountOrdineDetailBox from '../MyAccountOrdineDetailBox/MyAccountOrdineDetailBox'
import { Chat } from '@components/icons'
import styles from './MyAccountOrdineDetailLog.module.sass'
import { useSelector } from 'react-redux'
import { FormCheckbox, StatoOrdine, ButtonRounded } from '@components/atoms'

export interface MyAccountOrdineDetailLogProps {
  ordine?: OrdineDetailType
  chatBadge?: number
  onChatClick?: () => void
  className?: string
}

const MyAccountOrdineDetailLog = ({
  ordine,
  chatBadge,
  onChatClick,
  className,
}: MyAccountOrdineDetailLogProps) => {

  const t = useTrans()
  const { utente } = useSelector((state: RootStateType) => state?.utente)
  const userType = getUserType(utente)
  const [mostraOriginali, setMostraOriginali] = useState(false)
  useEffect(() => setMostraOriginali(false), [ordine?.id])

  const badge = 
    !!chatBadge
      ? { type: 'number' as const, value: chatBadge }
      : undefined

  const action = (
    <ButtonRounded
      size="sm"
      // TODO - decommentare quando abiliteremo la chat (sostituire label)
      // label={t("Chat & log")}
      label={t("Log attività ordine")}
      icon={<Chat />}
      badge={badge}
      onClick={onChatClick}
    />
  )

  const getOrdini = (o: OrdineType): OrdineType[] => o.isParent ? o.ordini : [o]

  const logs: (LogStatoOrdineType & LogStatoPosizionePersonalizzazioneOrdineType & { level: 'ordine' | 'posizione'})[] = useMemo(() => {
    return getOrdini(ordine).reduce((res, o) => res.concat((o.log?.filter(l => userType !== 'fornitore' || l.stato !== 'NEW_BLOCCATO').map(l => ({...l, level: 'ordine', numeroDocumento: o.numeroDocumento })) || []).concat(
      o.personalizzazioniOrdine?.reduce((resP, p) => resP.concat(
        p.posizioniPersonalizzazioneOrdine?.reduce((resPos, pos) => resPos.concat(
          pos.log?.map(l => ({...l, level: 'posizione', posizionePersonalizzazioneOrdine: pos})) || []
        ), []) || []
      ), []) || []
    )), []).sort((a, b) => a.created > b.created ? -1 : 1)
  }, [ordine, userType])

  const renderItems = () =>
    logs?.length ? (
      <ol className={styles.list}>
        {logs.map((item, index) => {
          const personalizationName = item.posizionePersonalizzazioneOrdine?.personalizzazioneNomeLavorazione
          const positionLabel = item.posizionePersonalizzazioneOrdine?.posizione?.nome
          const note = item.level === 'posizione' && !mostraOriginali
            ? item.noteTradotta || item.note
            : item.note
          const dateLabel = item.created ? dateTimeFormat(item.created) : t('Data non disponibile')
          const key = `${item.stato ?? 'stato'}-${item.id ?? 'ordine'}-${item.created ?? index}`

          return (
            <li key={key} className={cn(styles.item, styles['item--' + item.level])}>
              <time className={styles.date} dateTime={item.created}>
                {dateLabel}
              </time>
              <div className={styles.statusWrap}>
                {item.stato ? (
                  <StatoOrdine stato={item.stato} level={item.level} className={styles.status} />
                ) : (
                  <span className={styles.statusPlaceholder}>—</span>
                )}
              </div>
              {(item.level === 'posizione') ? (
                <div className={styles.details}>
                  {personalizationName ? (
                    <p className={styles.personalization}>
                      {t('Personalizzazione {0}', [personalizationName])}
                    </p>
                  ) : null}
                  {positionLabel || note ? (
                    <p className={styles.highlight}>
                      {positionLabel ? (
                        <span className={styles.highlightLabel}>{positionLabel}</span>
                      ) : null}
                      {note ? (
                        <span className={styles.highlightNote}>
                          {positionLabel ? ' - ' : null}
                          {note}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className={styles.details}>
                  {item.numeroDocumento ? (
                    <p className={styles.personalization}>
                      {t('Ordine {0}', [item.numeroDocumento])}
                    </p>
                  ) : null}
                  {item.note ? <p className={styles.highlight}>
                    <span className={styles.highlightNote}>
                      {t(note)}
                    </span>
                  </p>
                  : <></>}
                </div>
              )}
            </li>
          )
        })}
      </ol>
    ) : (
      <p className={styles.empty}>{t('Nessun log disponibile')}</p>
    )

  return (
    <MyAccountOrdineDetailBox
      title={t('Log ordine')}
      className={cn(styles.root, className)}
      headerClassName={styles.header}
      bodyClassName={styles.body}
      actions={action}
    >
      {logs.some(log => log.level === 'posizione' && log.noteTradotta && log.noteTradotta !== log.note) && (
        <FormCheckbox
          id={`ordine-detail-log-traduzioni-${ordine.id}`}
          size="sm"
          label={
            <>
              {t('Mostra testi originali')}
              {' — '}
              {t('Traduzione automatica generata da IA')}
            </>
          }
          checked={mostraOriginali}
          onChange={(event) => setMostraOriginali(event.target.checked)}
          className={styles.translationToggle}
        />
      )}
      {renderItems()}
    </MyAccountOrdineDetailBox>
  )
}

export default MyAccountOrdineDetailLog
